/**
 * Detection Wizard Modal
 * Lets user choose between Manual, POPIA AI, or AI Categories detection methods.
 * Shows cached indicators, credit estimates, and cross-method discounts.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  MousePointer,
  Shield,
  LayoutGrid,
  Check,
  AlertCircle,
  Loader,
} from 'lucide-react';

export default function DetectionWizardModal({
  isOpen,
  onClose,
  onSelectMethod,
  document,
  userCredits,
  isProcessing,
  isAnalyzing,
}) {
  const [selected, setSelected] = useState(null);

  // Reset selection each time the wizard opens
  useEffect(() => {
    if (isOpen) setSelected(null);
  }, [isOpen]);

  if (!document) return null;

  const analysis = document.analysis || {};
  const detections = document.detections || {};
  const totalPages = analysis.totalPages || document.pageCount || 1;
  const docType = analysis.documentType;
  const isTextBased = docType === 'text';

  // Credit calculations
  const creditsPerPage = isTextBased || !docType ? 1 : 5;
  const popiaCredits = totalPages * creditsPerPage;
  const categoriesCredits = Math.ceil(totalPages / 10); // 1 credit per 10 pages

  // Cross-method discounts
  const popiaCached = !!detections.popia;
  const categoriesCached = !!detections.categories;

  // POPIA cost: free if cached, discounted if Categories already ran
  let popiaEffectiveCredits = popiaCredits;
  if (popiaCached) {
    popiaEffectiveCredits = 0;
  } else if (categoriesCached) {
    const catCreditsUsed = detections.categories.creditsUsed || 0;
    popiaEffectiveCredits = Math.max(1, popiaCredits - catCreditsUsed);
  }

  // Categories cost: free if cached or if POPIA already ran
  let categoriesEffectiveCredits = categoriesCredits;
  if (categoriesCached) {
    categoriesEffectiveCredits = 0;
  } else if (popiaCached) {
    categoriesEffectiveCredits = 0;
  }

  const methods = [
    {
      id: 'categories',
      title: 'AI Categories',
      description: '15 categories detected via regex + AI. Fast and affordable.',
      icon: LayoutGrid,
      credits: categoriesEffectiveCredits,
      cached: categoriesCached,
      badge: '1 credit/10 pages',
      badgeColor: 'bg-blue-100 text-blue-700',
    },
    {
      id: 'popia',
      title: 'POPIA AI',
      description: 'AI detects personal information under SA POPIA compliance.',
      icon: Shield,
      credits: popiaEffectiveCredits,
      cached: popiaCached,
      disabled: false,
      badge: '1 credit/page',
      badgeColor: 'bg-emerald-100 text-emerald-700',
    },
    {
      id: 'manual',
      title: 'Manual',
      description: 'Draw redaction boxes and search for keywords manually.',
      icon: MousePointer,
      credits: 0,
      cached: false,
      disabled: false,
      badge: 'Free',
      badgeColor: 'bg-gray-100 text-gray-700',
    },
  ];

  // Show Categories unless analysis confirmed it's image-based.
  // When analysis hasn't completed yet (e.g. password-protected PDF), show all options.
  const isAnalyzed = !!docType;
  const availableMethods = methods.filter(m => m.id !== 'categories' || !isAnalyzed || isTextBased);

  const selectedMethod = methods.find((m) => m.id === selected);
  const hasInsufficientCredits =
    selectedMethod && selectedMethod.credits > 0 && userCredits < selectedMethod.credits;

  const handleConfirm = () => {
    if (!selected || hasInsufficientCredits) return;
    onSelectMethod(selected);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isProcessing && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose Detection Method</DialogTitle>
          <DialogDescription>
            Select how you want to detect sensitive information in this document.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {availableMethods.map((method) => {
            const Icon = method.icon;
            const isSelected = selected === method.id;
            const isCached = method.cached;

            return (
              <button
                key={method.id}
                onClick={() => setSelected(method.id)}
                disabled={isProcessing}
                className={`w-full text-left p-4 rounded-lg border-2 transition cursor-pointer
                  ${isSelected
                    ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }
                `}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${isSelected ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                    <Icon className={`w-5 h-5 ${isSelected ? 'text-emerald-600' : 'text-gray-500 dark:text-gray-400'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {method.title}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${method.badgeColor}`}>
                        {method.badge}
                      </span>
                      {isCached && (
                        <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-medium">
                          <Check className="w-3 h-3" />
                          Cached
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {method.description}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    {isCached ? (
                      <span className="text-sm font-bold text-green-600">Free</span>
                    ) : method.credits === 0 ? (
                      <span className="text-sm font-bold text-gray-600 dark:text-gray-300">Free</span>
                    ) : (
                      <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {method.credits} cr
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
          {!isTextBased && isAnalyzing && (
            <div className="flex items-center gap-2 p-3 text-sm text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              Checking for more options...
            </div>
          )}
        </div>

        {/* Credit summary when a paid method is selected */}
        {selectedMethod && selectedMethod.credits > 0 && (
          <div className={`p-3 rounded-lg border text-sm ${
            hasInsufficientCredits
              ? 'border-red-200 bg-red-50'
              : 'border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800'
          }`}>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Cost:</span>
              <span className="font-bold text-gray-900 dark:text-gray-100">
                {selectedMethod.credits} credit{selectedMethod.credits !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-gray-600 dark:text-gray-400">Your balance:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {userCredits} credits
              </span>
            </div>
            {hasInsufficientCredits && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                You need {selectedMethod.credits - userCredits} more credits.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected || hasInsufficientCredits || isProcessing}
            className="w-full sm:w-auto"
          >
            {isProcessing ? (
              <>
                <Loader className="w-4 h-4 animate-spin mr-2" />
                Processing...
              </>
            ) : selected ? (
              selectedMethod?.cached
                ? `View ${selectedMethod.title} Results`
                : selectedMethod?.credits > 0
                  ? `Run ${selectedMethod?.title} (${selectedMethod?.credits} credits)`
                  : `Use ${selectedMethod?.title}`
            ) : (
              'Select a method'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
